import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  AppBar,
  Tabs,
  Tab,
  Paper,
  Grid, 
  Card, 
  CardContent, 
  Divider,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress
} from '@mui/material';
import WeatherPanel from '../components/WeatherPanel';
import ScainPanel from '../components/ScainPanel';
import CameraPanel from '../components/CameraPanel';
import DashboardMyriotaTab from '../components/DashboardMyriotaTab';
import CommercialAdminPanel from '../components/CommercialAdminPanel';
import CustomerDirectoryPanel from '../components/CustomerDirectoryPanel';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, BarChart, Bar } from 'recharts';
import { API_URL } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab Panel Component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      style={{ width: '100%' }}
      {...other}
    >
      {value === index && (
        <div style={{ padding: '1.5rem' }}>
          {children}
        </div>
      )}
    </div>
  );
}

// Placeholder tab components
const DashboardTemperatureTab = ({ icestupaId, startDate, endDate }: any) => {
  const [hourlyData, setHourlyData] = useState<any[]>([]);
  const [hourlyLoading, setHourlyLoading] = useState(false);
  const [hourlyError, setHourlyError] = useState<string | null>(null);

  const [deviceOptions, setDeviceOptions] = useState<{source: string}[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<{source: string} | null>(null);
  const [deviceStats, setDeviceStats] = useState<any[]>([]);
  const [deviceStatsLoading, setDeviceStatsLoading] = useState(false);
  const [deviceStatsError, setDeviceStatsError] = useState<string | null>(null);

  // Fetch hourly data for all devices
  useEffect(() => {
    setHourlyLoading(true);
    setHourlyError(null);
    let url = `${API_URL}/dashboard/temperature_per_device_hourly?`;
    if (startDate) url += `start_date=${startDate}T00:00:00&`;
    if (endDate) url += `end_date=${endDate}T23:59:59&`;
    
    authenticatedFetch(url)
      .then(res => res.ok ? res.json() : res.json().then(e => { throw e; }))
      .then(data => {
        setHourlyData(data);
        // Build device options from data (use source)
        const opts = Array.from(new Set(data.map((d: any) => d.source)))
          .map((source: unknown, _idx: number) => ({ source: source as string }));
        setDeviceOptions(opts);
        // Default to scain01 if present, else first device
        if (opts.length > 0) {
          const scain01 = opts.find(o => o.source === 'scain01');
          setSelectedDevice(scain01 || opts[0]);
        } else {
          setSelectedDevice(null);
        }
      })
      .catch(e => setHourlyError(e.detail || e.message || 'Failed to fetch temperature per device'))
      .finally(() => setHourlyLoading(false));
    // eslint-disable-next-line
  }, [startDate, endDate]);

  // Fetch stats for selected device
  useEffect(() => {
    if (!selectedDevice) return;
    setDeviceStatsLoading(true);
    setDeviceStatsError(null);
    let url = `${API_URL}/dashboard/temperature_stats_for_device?source=${selectedDevice.source}`;
    if (startDate) url += `&start_date=${startDate}T00:00:00`;
    if (endDate) url += `&end_date=${endDate}T23:59:59`;
    authenticatedFetch(url)
      .then(res => res.ok ? res.json() : res.json().then(e => { throw e; }))
      .then(setDeviceStats)
      .catch(e => setDeviceStatsError(e.detail || e.message || 'Failed to fetch device temperature stats'))
      .finally(() => setDeviceStatsLoading(false));
  }, [selectedDevice, startDate, endDate]);

  // Prepare data for first chart
  const deviceKeys = Array.from(new Set(hourlyData.map(d => d.source)));
  const hourlyChartData = Array.from(new Set(hourlyData.map(d => d.hour))).sort().map(hour => {
    const entry: any = { hour };
    deviceKeys.forEach(source => {
      const found = hourlyData.find(d => d.hour === hour && d.source === source);
      entry[source] = found ? found.avg_temperature : null;
    });
    return entry;
  });

  return (
    <div>
      <Typography variant="h6" gutterBottom>Temperature per device, average per hour</Typography>
      {hourlyLoading ? <CircularProgress /> : hourlyError ? <Typography color="error">{hourlyError}</Typography> : (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={hourlyChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" tickFormatter={h => h?.slice(5, 16)} />
            <YAxis />
            <Tooltip />
            <Legend />
            {deviceKeys.map((source, idx) => (
              <Line
                key={source}
                type="monotone"
                dataKey={source}
                name={source}
                stroke={`hsl(${(idx * 60) % 360}, 70%, 50%)`}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
      {deviceOptions.length > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', margin: '32px 0 16px 0' }}>
          <label style={{ marginRight: 8, fontWeight: 500 }}>Device:</label>
          <select
            style={{ minWidth: 180, height: 32 }}
            value={selectedDevice ? selectedDevice.source : ''}
            onChange={e => {
              setSelectedDevice({ source: e.target.value });
            }}
          >
            {deviceOptions.map(opt => (
              <option key={opt.source} value={opt.source}>{opt.source}</option>
            ))}
          </select>
        </div>
      ) : (
        <div style={{ margin: '32px 0 16px 0', color: 'gray' }}>No devices available for this date range.</div>
      )}
      <Typography variant="h6" gutterBottom>Min, Max, Average temperature for selected device</Typography>
      {deviceStatsLoading ? <CircularProgress /> : deviceStatsError ? <Typography color="error">{deviceStatsError}</Typography> : (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={deviceStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" tickFormatter={h => h?.slice(5, 16)} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="min_temperature" name="Min Temp" stroke="#1976d2" dot={false} />
            <Line type="monotone" dataKey="avg_temperature" name="Avg Temp" stroke="#43a047" dot={false} />
            <Line type="monotone" dataKey="max_temperature" name="Max Temp" stroke="#e53935" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const DashboardProjectMetricsTab = ({ icestupaId, startDate, endDate }: any) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    let url = `${API_URL}/dashboard/icestupa_daily_metrics?`;
    if (startDate) url += `start_date=${startDate}&`;
    if (endDate) url += `end_date=${endDate}&`;
    authenticatedFetch(url)
      .then(res => res.ok ? res.json() : res.json().then(e => { throw e; }))
      .then(setData)
      .catch(e => setError(e.detail || e.message || 'Failed to fetch project metrics'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  // Group by date and icestupa_id for stacked bar charts
  const dates = Array.from(new Set(data.map(d => d.date))).sort();
  const icestupaIds = Array.from(new Set(data.map(d => d.icestupa_id)));

  // m3 per day: sum_flowrate_spraying + sum_flowrate_excess per icestupa per day
  const m3PerDayChartData = dates.map(date => {
    const entry: any = { date };
    icestupaIds.forEach(id => {
      const found = data.find(d => d.date === date && d.icestupa_id === id);
      entry[id] = found ? (Number(found.sum_flowrate_spraying || 0) + Number(found.sum_flowrate_excess || 0)) : 0;
    });
    return entry;
  });

  // m3 total: cumulative sum per icestupa per day
  const m3TotalCumulative: Record<string, number> = {};
  const m3TotalChartData = dates.map(date => {
    const entry: any = { date };
    icestupaIds.forEach(id => {
      const found = data.find(d => d.date === date && d.icestupa_id === id);
      const val = found ? (Number(found.sum_flowrate_spraying || 0) + Number(found.sum_flowrate_excess || 0)) : 0;
      m3TotalCumulative[id] = (m3TotalCumulative[id] || 0) + val;
      entry[id] = m3TotalCumulative[id];
    });
    return entry;
  });

  // count_hours_temp_below_2 per day, grouped by icestupa_id
  const below2ChartData = dates.map(date => {
    const entry: any = { date };
    icestupaIds.forEach(id => {
      const found = data.find(d => d.date === date && d.icestupa_id === id);
      entry[id] = found ? Number(found.count_hours_temp_below_2 || 0) : 0;
    });
    return entry;
  });

  // count_hours_temp_min_below_0 per day, grouped by icestupa_id
  const below0ChartData = dates.map(date => {
    const entry: any = { date };
    icestupaIds.forEach(id => {
      const found = data.find(d => d.date === date && d.icestupa_id === id);
      entry[id] = found ? Number(found.count_hours_temp_min_below_0 || 0) : 0;
    });
    return entry;
  });

  return (
    <div>
      {loading && <CircularProgress />}
      {error && <Typography color="error">{error}</Typography>}
      {!loading && !error && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">m3 per day</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={m3PerDayChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {icestupaIds.map((id, idx) => (
                    <Bar key={id} dataKey={id} stackId="a" fill={`hsl(${(idx * 60) % 360}, 70%, 50%)`} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">m3 total</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={m3TotalChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {icestupaIds.map((id, idx) => (
                    <Bar key={id} dataKey={id} stackId="a" fill={`hsl(${(idx * 60) % 360}, 70%, 50%)`} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">Hours count_hours_temp_below_2 per day, grouped by icestupa_id</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={below2ChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {icestupaIds.map((id, idx) => (
                    <Bar key={id} dataKey={id} stackId="a" fill={`hsl(${(idx * 60) % 360}, 70%, 50%)`} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">Hours count_hours_temp below 0 per day, grouped by icestupa_id</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={below0ChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {icestupaIds.map((id, idx) => (
                    <Bar key={id} dataKey={id} stackId="a" fill={`hsl(${(idx * 60) % 360}, 70%, 50%)`} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

const HighLevelMetricsBar = ({ startDate, endDate, icestupaId }: { startDate: string, endDate: string, icestupaId?: string }) => {
  const [metrics, setMetrics] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    let url = `${API_URL}/dashboard/icestupa_highlevel_metrics?`;
    if (startDate) url += `start_date=${startDate}&`;
    if (endDate) url += `end_date=${endDate}&`;
    authenticatedFetch(url)
      .then(res => res.ok ? res.json() : res.json().then(e => { throw e; }))
      .then(setMetrics)
      .catch(e => setError(e.detail || e.message || 'Failed to fetch high-level metrics'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return <div style={{ marginBottom: 16 }}><CircularProgress /></div>;
  if (error) return <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>;
  let filtered = metrics;
  if (icestupaId && icestupaId !== 'All') {
    filtered = metrics.filter(m => m.icestupa_id === icestupaId);
  }
  if (!filtered.length) return null;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>High-Level Stupa Metrics</Typography>
      <div style={{ overflowX: 'auto', userSelect: 'text' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>Icestupa</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Replenished (m³)</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Managed (m³)</th>
              <th style={{ padding: 8, textAlign: 'right' }}>% Time Freezing</th>
              <th style={{ padding: 8, textAlign: 'right' }}>% Time Spraying</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(m => (
              <tr key={m.icestupa_id}>
                <td style={{ padding: 8 }}>{m.icestupa_id}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{Math.round(Number(m.total_replenished || 0))}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{Math.round(Number(m.total_managed || 0))}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{Number(m.pct_time_freezing || 0).toFixed(1)}%</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{Number(m.pct_time_spraying || 0).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Paper>
  );
};

// Dashboard Component
const Dashboard = () => {
  const [tab, setTab] = useState<number>(0);
  const [icestupaId, setIcestupaId] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sourceEndDate, setSourceEndDate] = useState('');
  const [icestupaOptions, setIcestupaOptions] = useState<string[]>(['All']);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersError, setFiltersError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setFiltersLoading(true);
    setFiltersError(null);
    authenticatedFetch(`${API_URL}/dashboard/icestupas`)
      .then(async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.detail || 'Unable to load hydraulic devices');
        }
        return response.json();
      })
      .then((body) => {
        if (!active) return;
        setIcestupaOptions(['All', ...(body.icestupas || [])]);
        setStartDate(body.start_date || '');
        setEndDate(body.end_date || '');
        setSourceEndDate(body.end_date || '');
      })
      .catch((error) => {
        if (active) {
          setFiltersError(error instanceof Error ? error.message : 'Unable to load hydraulic devices');
        }
      })
      .finally(() => {
        if (active) setFiltersLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, value: number) => setTab(value);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <select
          style={{ minWidth: 180, marginRight: 16, height: 40 }}
          value={icestupaId}
          disabled={filtersLoading}
          onChange={e => setIcestupaId(e.target.value)}
        >
          {icestupaOptions.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <input
          type="date"
          value={startDate}
          disabled={filtersLoading}
          onChange={e => setStartDate(e.target.value)}
          style={{ marginRight: 16, height: 36 }}
        />
        <input
          type="date"
          value={endDate}
          disabled={filtersLoading}
          onChange={e => setEndDate(e.target.value)}
          style={{ height: 36 }}
        />
      </div>
      {filtersLoading && <CircularProgress size={20} sx={{ mb: 2 }} />}
      {filtersError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {filtersError}
        </Typography>
      )}
      {!filtersLoading && !filtersError && (
        <HighLevelMetricsBar
          startDate={startDate}
          endDate={endDate}
          icestupaId={icestupaId}
        />
      )}
      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Myriota data stream" />
        <Tab label="temperatures" />
        <Tab label="project metrics" />
      </Tabs>
      {tab === 0 && !filtersLoading && !filtersError && (
        <DashboardMyriotaTab
          icestupaId={icestupaId}
          startDate={startDate}
          endDate={endDate}
          sourceEndDate={sourceEndDate}
        />
      )}
      {tab === 1 && <DashboardTemperatureTab icestupaId={icestupaId} startDate={startDate} endDate={endDate} />}
      {tab === 2 && <DashboardProjectMetricsTab icestupaId={icestupaId} startDate={startDate} endDate={endDate} />}
    </div>
  );
};

// Main Admin Page Component
const AdminPage = () => {
  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <div style={{ 
      backgroundColor: '#f5f5f5', 
      minHeight: '100vh', 
      paddingTop: '7rem', 
      paddingBottom: '4rem' 
    }}>
      <Container maxWidth="xl">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '2rem' 
        }}>
          <Typography variant="h3" component="h1">
            Stupa Monitoring System
          </Typography>
        </div>
        
        <Paper style={{ marginBottom: '1.5rem' }}>
          <AppBar position="static" color="default" elevation={0}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Dashboard" />
              <Tab label="Image Gallery" aria-label="Image Gallery" />
              <Tab label="Access & Budgets" />
              <Tab label="Customer Pages" />
            </Tabs>
          </AppBar>
          
          <TabPanel value={tabIndex} index={0}>
            <Dashboard />
          </TabPanel>
          
          <TabPanel value={tabIndex} index={1}>
            <CameraPanel />
          </TabPanel>

          <TabPanel value={tabIndex} index={2}>
            <CommercialAdminPanel />
          </TabPanel>

          <TabPanel value={tabIndex} index={3}>
            <CustomerDirectoryPanel />
          </TabPanel>
        </Paper>
      </Container>
    </div>
  );
};

export default AdminPage; 